import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { barriersApi } from '../../services/settingsApi';
import { useTranslation } from '../../hooks/useTranslation';

export default function BarriersTab() {
  const [barriers, setBarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setStatusLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredBarriers, setFilteredBarriers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingBarrier, setEditingBarrier] = useState(null);
  const [isTestingSystem, setIsTestingSystem] = useState(false);
  const [isSyncingSystem, setIsSyncingSystem] = useState(false);
  const [systemStatus, setSystemStatus] = useState(null);
  const [formData, setFormData] = useState({
    barrier_code: '',
    name: '',
    location: '',
    barrier_type: '',
    connection_type: '',
    ip_address: '',
    port: '',
    api_endpoint: '',
    connection_config: '{}',
    toll_station_id: '',
    status: 'offline'
  });
  const [jsonError, setJsonError] = useState('');
  const { getMessage } = useTranslation();
  // API Test Panel States
  const [showTestPanel, setShowTestPanel] = useState(false);
  const [selectedBarrierForTest, setSelectedBarrierForTest] = useState(null);
  const [testResults, setTestResults] = useState({
    open: null,
    close: null,
    events: null,
    stats: null
  });
  const [testLoading, setTestLoading] = useState({
    open: false,
    close: false,
    events: false,
    stats: false
  });
  const [eventsParams, setEventsParams] = useState({
    limit: '10',
    event_type: '',
    from_date: '',
    to_date: '',
    uhf_card_id: '',
    obu_id: '',
    plate_number: '',
    gate_id: ''
  });
  const [statsParams, setStatsParams] = useState({
    period: 'day',
    from_date: '',
    to_date: ''
  });

  const fetchBarriers = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await barriersApi.getAll();
      const barriersData = response.data.data || response.data || [];

      // Debug: Log first barrier to verify all fields are present
      if (barriersData.length > 0) {
        console.log('=== BARRIER DATA CHECK ===');
        console.log('First barrier fields:', Object.keys(barriersData[0]));
        console.log('Has api_endpoint field:', 'api_endpoint' in barriersData[0]);
        console.log('api_endpoint value:', barriersData[0].api_endpoint);
        console.log('Has ip_address field:', 'ip_address' in barriersData[0]);
        console.log('ip_address value:', barriersData[0].ip_address);
        console.log('Has port field:', 'port' in barriersData[0]);
        console.log('port value:', barriersData[0].port);
        console.log('Sample barrier:', barriersData[0]);
        console.log('Total barriers:', barriersData.length);
      }

      setBarriers(barriersData);
    } catch (error) {
      console.error('Error fetching barriers:', error);
      toast.error('Failed to load barriers');
      setBarriers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSystemStatus = React.useCallback(async () => {
    try {
      const res = await barriersApi.status();
      setSystemStatus(res.data);
    } catch (error) {
      console.warn('Failed to fetch barrier system status');
    }
  }, []);

  useEffect(() => {
    fetchBarriers();
    fetchSystemStatus();
  }, [fetchBarriers, fetchSystemStatus]);

  const handleTestSystem = async () => {
    try {
      setIsTestingSystem(true);
      const res = await barriersApi.testConnection();
      toast.success(res.data?.message || 'Connection test successful');
      await fetchSystemStatus();
    } catch (error) {
      console.error('Error testing system connection:', error);
      toast.error('Connection test failed');
    } finally {
      setIsTestingSystem(false);
    }
  };

  const handleSyncSystem = async () => {
    try {
      setIsSyncingSystem(true);
      const res = await barriersApi.sync();
      toast.success(res.data?.message || 'Data synchronization completed');
    } catch (error) {
      console.error('Error syncing barrier system:', error);
      toast.error('Sync failed');
    } finally {
      setIsSyncingSystem(false);
    }
  };


  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleAddBarrier = () => {
    // Show modal with all barrier information from database
    setShowModal(true);
    setEditingBarrier(null);
    setFormData({
      barrier_code: '',
      name: '',
      location: '',
      barrier_type: '',
      connection_type: '',
      ip_address: '',
      port: '',
      api_endpoint: '',
      connection_config: '{}',
      toll_station_id: '',
      status: 'offline'
    });
  };

  const handleEditBarrier = (barrier) => {
    setEditingBarrier(barrier);
    setFormData({
      barrier_code: barrier.barrier_code || '',
      name: barrier.name || '',
      location: barrier.location || '',
      barrier_type: barrier.barrier_type || '',
      connection_type: barrier.connection_type || '',
      ip_address: barrier.ip_address || '',
      port: barrier.port || '',
      api_endpoint: barrier.api_endpoint || '',
      connection_config: barrier.connection_config ? JSON.stringify(barrier.connection_config, null, 2) : '{}',
      toll_station_id: barrier.toll_station_id || '',
      status: barrier.status || 'offline'
    });
    setShowModal(true);
  };

  const handleDeleteBarrier = async (barrier) => {
    if (!window.confirm('Are you sure you want to delete this barrier?')) {
      return;
    }

    try {
      await barriersApi.delete(barrier.id);
      toast.success('Barrier deleted successfully');
      fetchBarriers();
    } catch (error) {
      console.error('Error deleting barrier:', error);
      toast.error('Failed to delete barrier');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('========== SAVING BARRIER ==========');
    console.log('Form Data:', formData);

    try {
      // Validate and parse JSON
      let parsedConfig = null;

      if (formData.connection_config && formData.connection_config.trim() !== '') {
        try {
          parsedConfig = JSON.parse(formData.connection_config);
          console.log('Parsed connection_config:', parsedConfig);
        } catch (jsonError) {
          console.error('Invalid JSON in connection_config:', jsonError);
          toast.error('Invalid JSON format in Connection Config. Please check your JSON syntax.');
          return; // Stop submission if JSON is invalid
        }
      }

      const data = {
        barrier_code: formData.barrier_code,
        name: formData.name,
        location: formData.location,
        barrier_type: formData.barrier_type,
        connection_type: formData.connection_type,
        ip_address: formData.ip_address || null,
        port: formData.port ? parseInt(formData.port) : null,
        api_endpoint: formData.api_endpoint || null,
        connection_config: parsedConfig,
        toll_station_id: formData.toll_station_id ? parseInt(formData.toll_station_id) : null,
        status: formData.status
      };

      console.log('Data to be sent:', JSON.stringify(data, null, 2));
      console.log('Field details:');
      console.log('  ip_address (formData):', formData.ip_address);
      console.log('  ip_address (data):', data.ip_address);
      console.log('  port (formData):', formData.port);
      console.log('  port (data):', data.port);
      console.log('  api_endpoint (formData):', formData.api_endpoint);
      console.log('  api_endpoint (data):', data.api_endpoint);

      if (editingBarrier) {
        console.log('Updating barrier ID:', editingBarrier.id);
        const response = await barriersApi.update(editingBarrier.id, data);
        console.log('Update response:', response.data);
        toast.success('Barrier updated successfully');
      } else {
        console.log('Creating new barrier');
        const response = await barriersApi.create(data);
        console.log('Create response:', response.data);
        toast.success('Barrier created successfully');
      }

      console.log('====================================');
      setShowModal(false);
      fetchBarriers();
    } catch (error) {
      console.error('Error saving barrier:', error);
      console.error('Error details:', error.response?.data);
      toast.error(`Failed to save barrier: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Validate JSON if changing connection_config
    if (name === 'connection_config') {
      if (value.trim() === '') {
        setJsonError('');
      } else {
        try {
          JSON.parse(value);
          setJsonError('');
        } catch (error) {
          setJsonError('Invalid JSON format');
        }
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormatJson = () => {
    try {
      const parsed = JSON.parse(formData.connection_config);
      const formatted = JSON.stringify(parsed, null, 2);
      setFormData(prev => ({ ...prev, connection_config: formatted }));
      setJsonError('');
      toast.success('JSON formatted successfully');
    } catch (error) {
      toast.error('Cannot format invalid JSON');
    }
  };

  const handleTestBarrier = async (barrier) => {
    try {
      setStatusLoading(true);
      const response = await barriersApi.test(barrier.id);
      toast.success(`Test: ${response.data?.data?.test_result?.message || 'OK'}`);
    } catch (error) {
      console.error('Error testing barrier:', error);
      toast.error('Test failed');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleOpenBarrier = async (barrier) => {
    try {
      const response = await barriersApi.open(barrier.id, { reason: 'Manual operation' });
      toast.success(response.data?.message || 'Barrier opened');
      fetchBarriers();
    } catch (error) {
      console.error('Error opening barrier:', error);
      toast.error('Failed to open barrier');
    }
  };

  const handleCloseBarrier = async (barrier) => {
    try {
      const response = await barriersApi.close(barrier.id, { reason: 'Manual operation' });
      toast.success(response.data?.message || 'Barrier closed');
      fetchBarriers();
    } catch (error) {
      console.error('Error closing barrier:', error);
      toast.error('Failed to close barrier');
    }
  };

  // API Test Functions
  const handleOpenTestPanel = (barrier) => {
    setSelectedBarrierForTest(barrier);
    setShowTestPanel(true);
    setTestResults({ open: null, close: null, events: null, stats: null });
  };

  const handleTestOpen = async () => {
    if (!selectedBarrierForTest) return;

    // Call barrierAPI.js endpoint directly (port 3002)
    const barrierApiUrl = 'http://localhost:3002/api/barrier/open';

    console.log('========== OPEN BARRIER TEST (Direct Hardware API) ==========');
    console.log('Endpoint: POST', barrierApiUrl);
    console.log('Barrier:', selectedBarrierForTest.name);

    setTestLoading(prev => ({ ...prev, open: true }));
    try {
      const response = await fetch(barrierApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      console.log('Response Status:', response.status);
      console.log('Response Data:', JSON.stringify(data, null, 2));
      console.log('============================================================');

      if (response.ok) {
        setTestResults(prev => ({
          ...prev,
          open: { success: true, data: data, timestamp: new Date().toISOString() }
        }));
        toast.success('Open command sent successfully');
      } else {
        throw new Error(data.error || 'Open command failed');
      }
    } catch (error) {
      console.error('Response Error:', error.message);
      console.log('============================================================');

      setTestResults(prev => ({
        ...prev,
        open: { success: false, error: error.message, timestamp: new Date().toISOString() }
      }));
      toast.error('Open command failed: ' + error.message);
    } finally {
      setTestLoading(prev => ({ ...prev, open: false }));
    }
  };

  const handleTestClose = async () => {
    if (!selectedBarrierForTest) return;

    // Call barrierAPI.js endpoint directly (port 3002)
    const barrierApiUrl = 'http://localhost:3002/api/barrier/close';

    console.log('========== CLOSE BARRIER TEST (Direct Hardware API) ==========');
    console.log('Endpoint: POST', barrierApiUrl);
    console.log('Barrier:', selectedBarrierForTest.name);

    setTestLoading(prev => ({ ...prev, close: true }));
    try {
      const response = await fetch(barrierApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      console.log('Response Status:', response.status);
      console.log('Response Data:', JSON.stringify(data, null, 2));
      console.log('=============================================================');

      if (response.ok) {
        setTestResults(prev => ({
          ...prev,
          close: { success: true, data: data, timestamp: new Date().toISOString() }
        }));
        toast.success('Close command sent successfully');
      } else {
        throw new Error(data.error || 'Close command failed');
      }
    } catch (error) {
      console.error('Response Error:', error.message);
      console.log('=============================================================');

      setTestResults(prev => ({
        ...prev,
        close: { success: false, error: error.message, timestamp: new Date().toISOString() }
      }));
      toast.error('Close command failed: ' + error.message);
    } finally {
      setTestLoading(prev => ({ ...prev, close: false }));
    }
  };

  const handleTestEvents = async () => {
    if (!selectedBarrierForTest) return;

    // Call barrierAPI.js endpoint directly (port 3002)
    // Note: This receives events FROM barrier hardware
    const barrierApiUrl = 'http://localhost:3002/api/barrier/events';

    setTestLoading(prev => ({ ...prev, events: true }));
    try {
      // Build request body with filter parameters
      const requestBody = {};
      if (eventsParams.limit) requestBody.limit = parseInt(eventsParams.limit);
      if (eventsParams.event_type) requestBody.event_type = eventsParams.event_type;
      if (eventsParams.from_date) requestBody.from_date = eventsParams.from_date;
      if (eventsParams.to_date) requestBody.to_date = eventsParams.to_date;
      if (eventsParams.uhf_card_id) requestBody.uhf_card_id = eventsParams.uhf_card_id;
      if (eventsParams.obu_id) requestBody.obu_id = eventsParams.obu_id;
      if (eventsParams.plate_number) requestBody.plate_number = eventsParams.plate_number;
      if (eventsParams.gate_id) requestBody.gate_id = eventsParams.gate_id;

      console.log('========== GET EVENTS TEST (Direct Hardware API) ==========');
      console.log('Endpoint: POST', barrierApiUrl);
      console.log('Request Body:', JSON.stringify(requestBody, null, 2));

      const response = await fetch(barrierApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      console.log('Response Status:', response.status);
      console.log('Response Data:', JSON.stringify(data, null, 2));
      console.log('===========================================================');

      if (response.ok) {
        setTestResults(prev => ({
          ...prev,
          events: { success: true, data: data, timestamp: new Date().toISOString() }
        }));
        toast.success('Events processed successfully');
      } else {
        throw new Error(data.error || 'Events request failed');
      }
    } catch (error) {
      console.error('Response Error:', error.message);
      console.log('===========================================================');

      setTestResults(prev => ({
        ...prev,
        events: { success: false, error: error.message, timestamp: new Date().toISOString() }
      }));
      toast.error('Events fetch failed: ' + error.message);
    } finally {
      setTestLoading(prev => ({ ...prev, events: false }));
    }
  };

  const handleTestStats = async () => {
    if (!selectedBarrierForTest) return;

    // Call barrierAPI.js endpoint directly (port 3002)
    const barrierApiUrl = 'http://localhost:3002/api/barrier/stats';

    setTestLoading(prev => ({ ...prev, stats: true }));
    try {
      // Build query string
      const queryParams = new URLSearchParams();
      if (statsParams.period) queryParams.append('period', statsParams.period);
      if (statsParams.from_date) queryParams.append('from_date', statsParams.from_date);
      if (statsParams.to_date) queryParams.append('to_date', statsParams.to_date);

      const url = queryParams.toString() ? `${barrierApiUrl}?${queryParams}` : barrierApiUrl;

      console.log('========== GET STATS TEST (Direct Hardware API) ==========');
      console.log('Endpoint: GET', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      console.log('Response Status:', response.status);
      console.log('Response Data:', JSON.stringify(data, null, 2));
      console.log('===========================================================');

      if (response.ok) {
        setTestResults(prev => ({
          ...prev,
          stats: { success: true, data: data, timestamp: new Date().toISOString() }
        }));
        toast.success('Stats fetched successfully');
      } else {
        throw new Error(data.error || 'Stats request failed');
      }
    } catch (error) {
      console.error('Response Error:', error.message);
      console.log('===========================================================');

      setTestResults(prev => ({
        ...prev,
        stats: { success: false, error: error.message, timestamp: new Date().toISOString() }
      }));
      toast.error('Stats fetch failed: ' + error.message);
    } finally {
      setTestLoading(prev => ({ ...prev, stats: false }));
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBarrier(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      'inactive': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
      'maintenance': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
      'error': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
    };
    return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
  };


  return (
    <div className="space-y-6">
      {/* System Status Card */}
      {systemStatus && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="text-sm text-gray-600 dark:text-gray-400">System Status</div>
              <div className="text-lg font-semibold">
                {systemStatus.status || 'unknown'}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-gray-500">Total</div>
                <div className="font-medium">{systemStatus.total_barriers ?? '-'}
                </div>
              </div>
              <div>
                <div className="text-gray-500">Active</div>
                <div className="font-medium">{systemStatus.active_barriers ?? '-'}</div>
              </div>
              <div>
                <div className="text-gray-500">Inactive</div>
                <div className="font-medium">{systemStatus.inactive_barriers ?? '-'}</div>
              </div>
              <div>
                <div className="text-gray-500">Maintenance</div>
                <div className="font-medium">{systemStatus.maintenance_barriers ?? '-'}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            🚧 {getMessage('settings.barriers') || 'Barrier System'}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {getMessage('settings.barriers_desc') || 'Manage barrier system integration and control'}
          </p>
        </div>
        <button
          onClick={handleAddBarrier}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
        >
          ➕ Add Barrier
        </button>
      </div>

      {/* Search */}
      <div className="flex justify-between items-center">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search barriers..."
            value={searchQuery}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
            🔍
          </div>
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {filteredBarriers.length} barrier(s) found
        </div>
      </div>

      {/* Barriers Table */}
      <div className="bg-white dark:bg-gray-700 rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-gray-600 dark:text-gray-400">Loading...</span>
          </div>
        ) : filteredBarriers.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 dark:text-gray-500 text-lg mb-2">🚧</div>
            <p className="text-gray-600 dark:text-gray-400">
              {searchQuery ? 'No barriers found matching your search' : 'No barriers available'}
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Connection
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
              {filteredBarriers.map((barrier) => (
                <tr key={barrier.id} className="hover:bg-gray-50 dark:hover:bg-gray-600">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono text-gray-900 dark:text-white">
                      {barrier.barrier_code || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {barrier.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {barrier.barrier_type || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {barrier.connection_type || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(barrier.status)}`}>
                      {barrier.status || 'Unknown'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleOpenTestPanel(barrier)}
                      className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300 text-xs"
                    >
                      🧪 API Test
                    </button>
                    <button
                      onClick={() => handleEditBarrier(barrier)}
                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 text-xs"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDeleteBarrier(barrier)}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 text-xs"
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Modal - Redesigned */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-7xl mx-4 max-h-[95vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {editingBarrier ? '✏️ Edit Barrier' : '➕ Add New Barrier'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {editingBarrier ? 'Update barrier configuration' : 'All Barriers in Database'}
                </p>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-3xl font-bold"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {/* Show all barriers table when adding new */}
              {!editingBarrier && (
                <div className="mb-6">
                  <div className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <span className="text-2xl">ℹ️</span>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                          Current Barriers in Database - Complete View
                        </h3>
                        <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                          <p>Below is a complete list of all barriers currently stored in the MySQL database.</p>
                          <p className="mt-1">Total Barriers: <strong>{barriers.length}</strong></p>
                          <p className="mt-2 font-semibold">📊 Showing ALL 15 Database Columns:</p>
                          <div className="mt-1 text-xs grid grid-cols-3 gap-1">
                            <div>✅ 1. ID</div>
                            <div>✅ 2. Barrier Code</div>
                            <div>✅ 3. Name</div>
                            <div>✅ 4. Location</div>
                            <div>✅ 5. Type</div>
                            <div>✅ 6. Connection</div>
                            <div>⭐ 7. IP Address</div>
                            <div>⭐ 8. Port</div>
                            <div>⭐ 9. API Endpoint</div>
                            <div>✅ 10. Status</div>
                            <div>✅ 11. Station ID</div>
                            <div>⭐ 12. Last Ping</div>
                            <div>⭐ 13. Created At</div>
                            <div>⭐ 14. Updated At</div>
                            <div>✅ 15. Config (JSON)</div>
                          </div>
                          <p className="mt-2 text-xs italic">⭐ = New fields added | ✅ = Previously visible</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Field Statistics Card */}
                  {barriers.length > 0 && (
                    <div className="mb-4 p-4 bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700 rounded-lg">
                      <h4 className="text-sm font-semibold text-green-800 dark:text-green-200 mb-2">
                        📊 Database Field Coverage
                      </h4>
                      <div className="grid grid-cols-5 gap-2 text-xs">
                        <div className="bg-white dark:bg-gray-800 p-2 rounded">
                          <div className="text-gray-600 dark:text-gray-400">IP Addresses</div>
                          <div className="font-bold text-green-600">{barriers.filter(b => b.ip_address).length}/{barriers.length}</div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-2 rounded">
                          <div className="text-gray-600 dark:text-gray-400">Ports Set</div>
                          <div className="font-bold text-green-600">{barriers.filter(b => b.port).length}/{barriers.length}</div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-2 rounded">
                          <div className="text-gray-600 dark:text-gray-400">Endpoints</div>
                          <div className="font-bold text-green-600">{barriers.filter(b => b.api_endpoint).length}/{barriers.length}</div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-2 rounded">
                          <div className="text-gray-600 dark:text-gray-400">Last Ping</div>
                          <div className="font-bold text-green-600">{barriers.filter(b => b.last_ping).length}/{barriers.length}</div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-2 rounded">
                          <div className="text-gray-600 dark:text-gray-400">JSON Configs</div>
                          <div className="font-bold text-green-600">{barriers.filter(b => b.connection_config).length}/{barriers.length}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Barriers Database Table */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    {/* Scroll Indicator */}
                    <div className="bg-yellow-50 dark:bg-yellow-900 border-b border-yellow-200 dark:border-yellow-700 px-4 py-2">
                      <p className="text-xs text-yellow-800 dark:text-yellow-200 flex items-center">
                        <span className="mr-2">👉</span>
                        <strong>Scroll horizontally</strong> to view all 15 columns: ID, Code, Name, Location, Type, Connection, IP, Port, Endpoint, Status, Station, Last Ping, Created, Updated, Config
                        <span className="ml-2">👈</span>
                      </p>
                    </div>
                    <div className="overflow-x-auto" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-100 dark:bg-gray-800">
                          <tr>
                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                              ID
                            </th>
                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                              Barrier Code
                            </th>
                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                              Name
                            </th>
                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                              Location
                            </th>
                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                              Type
                            </th>
                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                              Connection
                            </th>
                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                              IP Address
                            </th>
                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                              Port
                            </th>
                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                              API Endpoint
                            </th>
                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                              Station ID
                            </th>
                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                              Last Ping
                            </th>
                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                              Created At
                            </th>
                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                              Updated At
                            </th>
                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                              Config
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {barriers.length === 0 ? (
                            <tr>
                              <td colSpan="15" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                                <div className="text-4xl mb-2">🚧</div>
                                <p>No barriers found in database</p>
                                <p className="text-sm mt-1">Fill the form below to create your first barrier</p>
                              </td>
                            </tr>
                          ) : (
                            barriers.map((barrier) => (
                              <tr key={barrier.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                  #{barrier.id}
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap">
                                  <span className="text-sm font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-gray-900 dark:text-white">
                                    {barrier.barrier_code || 'N/A'}
                                  </span>
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap">
                                  <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                    {barrier.name}
                                  </div>
                                  {barrier.station_name && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      {barrier.station_name}
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 py-3">
                                  <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                                    {barrier.location || '-'}
                                  </div>
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                                    {barrier.barrier_type || 'N/A'}
                                  </span>
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                    {barrier.connection_type || 'N/A'}
                                  </span>
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap">
                                  <div className="text-sm font-mono text-gray-900 dark:text-white">
                                    {barrier.ip_address || '-'}
                                  </div>
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {barrier.port || '-'}
                                </td>
                                <td className="px-3 py-3">
                                  <div className="text-xs font-mono text-gray-900 dark:text-white max-w-xs truncate">
                                    {barrier.api_endpoint || '-'}
                                  </div>
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(barrier.status)}`}>
                                    {barrier.status || 'Unknown'}
                                  </span>
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {barrier.toll_station_id || '-'}
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                                  {barrier.last_ping ? (
                                    <div>
                                      <div>{new Date(barrier.last_ping).toLocaleDateString()}</div>
                                      <div className="text-xs text-gray-400">{new Date(barrier.last_ping).toLocaleTimeString()}</div>
                                    </div>
                                  ) : '-'}
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                                  {barrier.created_at ? (
                                    <div>
                                      <div>{new Date(barrier.created_at).toLocaleDateString()}</div>
                                      <div className="text-xs text-gray-400">{new Date(barrier.created_at).toLocaleTimeString()}</div>
                                    </div>
                                  ) : '-'}
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                                  {barrier.updated_at ? (
                                    <div>
                                      <div>{new Date(barrier.updated_at).toLocaleDateString()}</div>
                                      <div className="text-xs text-gray-400">{new Date(barrier.updated_at).toLocaleTimeString()}</div>
                                    </div>
                                  ) : '-'}
                                </td>
                                <td className="px-3 py-3">
                                  {barrier.connection_config ? (
                                    <details className="text-xs">
                                      <summary className="cursor-pointer text-blue-600 dark:text-blue-400 hover:underline">
                                        View JSON
                                      </summary>
                                      <pre className="mt-2 p-2 bg-gray-100 dark:bg-gray-900 rounded text-xs overflow-x-auto max-w-md">
                                        {JSON.stringify(barrier.connection_config, null, 2)}
                                      </pre>
                                    </details>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900 dark:to-blue-900 rounded-lg border border-green-200 dark:border-green-700">
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">
                      📝 Create New Barrier
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Fill out the form below to add a new barrier to the system.
                    </p>
                  </div>
                </div>
              )}

              {/* Form Section */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Barrier Code *
                  </label>
                  <input
                    type="text"
                    name="barrier_code"
                    value={formData.barrier_code}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Barrier Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="e.g., Main Entrance, Exit Gate A"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Barrier Type *
                  </label>
                  <select
                    name="barrier_type"
                    value={formData.barrier_type}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Type</option>
                    {['entry', 'exit', 'both'].map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Connection Type *
                  </label>
                  <select
                    name="connection_type"
                    value={formData.connection_type}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Connection</option>
                    {['http', 'tcp', 'serial', 'modbus'].map(c => (
                      <option key={c} value={c}>{c.toUpperCase()}</option>
                    ))}
                  </select>
                </div>

                {/* Connection Details Section */}
                <div className="border-t border-gray-200 dark:border-gray-600 pt-4 mt-4">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                    <span className="mr-2">🔌</span>
                    Connection Details
                  </h4>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        IP Address
                      </label>
                      <input
                        type="text"
                        name="ip_address"
                        value={formData.ip_address || ''}
                        onChange={handleInputChange}
                        placeholder="e.g., 192.168.1.100"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Port
                      </label>
                      <input
                        type="number"
                        name="port"
                        value={formData.port || ''}
                        onChange={handleInputChange}
                        placeholder="e.g., 8080"
                        min="1"
                        max="65535"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      API Endpoint
                    </label>
                    <input
                      type="text"
                      name="api_endpoint"
                      value={formData.api_endpoint || ''}
                      onChange={handleInputChange}
                      placeholder="e.g., /api/barrier or /v1/gate/control"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      💡 Optional: Specify the API endpoint path for this barrier
                    </p>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Connection Config (JSON)
                    </label>
                    <button
                      type="button"
                      onClick={handleFormatJson}
                      className="text-xs bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 px-2 py-1 rounded"
                    >
                      📝 Format JSON
                    </button>
                  </div>
                  <textarea
                    name="connection_config"
                    value={formData.connection_config}
                    onChange={handleInputChange}
                    rows={6}
                    placeholder={`{
  "base_url": "http://192.168.100.224:8080/api/barrier",
  "endpoints": {
    "open": "/open",
    "close": "/close",
    "events": "/events",
    "stats": "/stats"
  }
}`}
                    className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 font-mono text-xs ${jsonError
                        ? 'border-red-500 dark:border-red-500'
                        : 'border-gray-300 dark:border-gray-600'
                      }`}
                  />
                  {jsonError && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                      ❌ {jsonError}
                    </p>
                  )}
                  {!jsonError && formData.connection_config && formData.connection_config.trim() !== '' && (
                    <p className="mt-1 text-xs text-green-600 dark:text-green-400">
                      ✅ Valid JSON format
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Toll Station ID (optional)
                  </label>
                  <input
                    type="number"
                    name="toll_station_id"
                    value={formData.toll_station_id}
                    onChange={handleInputChange}
                    placeholder="e.g., 1"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="offline">Offline</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                  >
                    {editingBarrier ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* API Test Panel Modal */}
      {showTestPanel && selectedBarrierForTest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                🧪 Barrier API Test Panel - {selectedBarrierForTest.name}
              </h3>
              <button
                onClick={() => setShowTestPanel(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl"
              >
                ✕
              </button>
            </div>

            {/* API Config Reference */}
            <details className="mb-4 p-3 bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg">
              <summary className="cursor-pointer font-semibold text-blue-900 dark:text-blue-100 hover:text-blue-600">
                📚 API Configuration & Expected Response Formats
              </summary>
              <div className="mt-3 space-y-3 text-xs">
                {/* Base URL */}
                <div className="p-2 bg-white dark:bg-gray-800 rounded">
                  <p className="font-semibold text-gray-900 dark:text-white">Base URL:</p>
                  <code className="text-blue-600 dark:text-blue-400">http://192.168.100.224:8080/api/barrier</code>
                </div>

                {/* Endpoints */}
                <div className="p-2 bg-white dark:bg-gray-800 rounded">
                  <p className="font-semibold text-gray-900 dark:text-white mb-1">Endpoints:</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>• <code className="text-green-600 dark:text-green-400">POST /open</code></div>
                    <div>• <code className="text-red-600 dark:text-red-400">POST /close</code></div>
                    <div>• <code className="text-blue-600 dark:text-blue-400">POST /events</code></div>
                    <div>• <code className="text-purple-600 dark:text-purple-400">GET /stats</code></div>
                  </div>
                </div>

                {/* UHF Event Format */}
                <div className="p-2 bg-white dark:bg-gray-800 rounded">
                  <p className="font-semibold text-gray-900 dark:text-white">UHF Card Reader Event:</p>
                  <pre className="text-xs mt-1 text-gray-700 dark:text-gray-300 overflow-x-auto">{`{
  "gate_id": "GATE-01",
  "timestamp": "2025-10-25T09:35:22Z",
  "event_type": "entry|exit|error",
  "status_code": 200|400|403|500,
  "uhf_card_id": "UHF1234567890",
  "plate_number": "AB-1234"
}`}</pre>
                </div>

                {/* OBU/RBU Event Format */}
                <div className="p-2 bg-white dark:bg-gray-800 rounded">
                  <p className="font-semibold text-gray-900 dark:text-white">OBU/RBU Vehicle Unit Event:</p>
                  <pre className="text-xs mt-1 text-gray-700 dark:text-gray-300 overflow-x-auto">{`{
  "gate_id": "TERMINAL-02",
  "timestamp": "2025-10-25T09:36:47Z",
  "event_type": "entry|exit|error",
  "status_code": 200|400|403|500,
  "obu_id": "OBU99887766",
  "plate_number": "CD-5678"
}`}</pre>
                </div>

                {/* Status Codes */}
                <div className="p-2 bg-white dark:bg-gray-800 rounded">
                  <p className="font-semibold text-gray-900 dark:text-white mb-1">Status Codes:</p>
                  <div className="grid grid-cols-2 gap-1 text-gray-700 dark:text-gray-300">
                    <div>• <span className="text-green-600">200</span> - Success</div>
                    <div>• <span className="text-orange-600">400</span> - Bad Request</div>
                    <div>• <span className="text-red-600">403</span> - Unauthorized</div>
                    <div>• <span className="text-red-700">500</span> - Internal Device Error</div>
                  </div>
                </div>
              </div>
            </details>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* 1. OPEN Test */}
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  🟢 1. Open Barrier
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Endpoint: POST /api/barriers/{'{id}'}/open
                </p>
                <button
                  onClick={handleTestOpen}
                  disabled={testLoading.open}
                  className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white px-4 py-2 rounded-lg mb-3"
                >
                  {testLoading.open ? '⏳ Testing...' : '🟢 Test Open'}
                </button>
                {testResults.open && (
                  <div className={`p-3 rounded-lg ${testResults.open.success
                      ? 'bg-green-100 dark:bg-green-900'
                      : 'bg-red-100 dark:bg-red-900'
                    }`}>
                    <div className="text-xs font-mono text-gray-900 dark:text-white mb-1">
                      {testResults.open.timestamp}
                    </div>
                    <div className="text-sm font-semibold mb-2">
                      {testResults.open.success ? '✅ Success' : '❌ Failed'}
                    </div>
                    <pre className="text-xs overflow-auto max-h-40 bg-white dark:bg-gray-800 p-2 rounded">
                      {JSON.stringify(testResults.open.success ? testResults.open.data : testResults.open.error, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              {/* 2. CLOSE Test */}
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  🔴 2. Close Barrier
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Endpoint: POST /api/barriers/{'{id}'}/close
                </p>
                <button
                  onClick={handleTestClose}
                  disabled={testLoading.close}
                  className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white px-4 py-2 rounded-lg mb-3"
                >
                  {testLoading.close ? '⏳ Testing...' : '🔴 Test Close'}
                </button>
                {testResults.close && (
                  <div className={`p-3 rounded-lg ${testResults.close.success
                      ? 'bg-green-100 dark:bg-green-900'
                      : 'bg-red-100 dark:bg-red-900'
                    }`}>
                    <div className="text-xs font-mono text-gray-900 dark:text-white mb-1">
                      {testResults.close.timestamp}
                    </div>
                    <div className="text-sm font-semibold mb-2">
                      {testResults.close.success ? '✅ Success' : '❌ Failed'}
                    </div>
                    <pre className="text-xs overflow-auto max-h-40 bg-white dark:bg-gray-800 p-2 rounded">
                      {JSON.stringify(testResults.close.success ? testResults.close.data : testResults.close.error, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              {/* 3. EVENTS Test */}
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  📋 3. Get Events
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Endpoint: POST /api/barriers/{'{id}'}/events
                </p>
                <details className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  <summary className="cursor-pointer hover:text-blue-600">📄 Expected Response Format</summary>
                  <div className="mt-2 p-2 bg-gray-100 dark:bg-gray-900 rounded">
                    <p className="font-semibold">UHF Events:</p>
                    <code className="text-xs">gate_id, timestamp, event_type, status_code, uhf_card_id, plate_number</code>
                    <p className="font-semibold mt-1">OBU/RBU Events:</p>
                    <code className="text-xs">gate_id, timestamp, event_type, status_code, obu_id, plate_number</code>
                  </div>
                </details>

                <div className="space-y-2 mb-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-600 dark:text-gray-400">Limit</label>
                      <input
                        type="number"
                        value={eventsParams.limit}
                        onChange={(e) => setEventsParams(prev => ({ ...prev, limit: e.target.value }))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 dark:text-gray-400">Event Type</label>
                      <select
                        value={eventsParams.event_type}
                        onChange={(e) => setEventsParams(prev => ({ ...prev, event_type: e.target.value }))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="">All</option>
                        <option value="entry">Entry</option>
                        <option value="exit">Exit</option>
                        <option value="error">Error</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-600 dark:text-gray-400">From Date</label>
                      <input
                        type="datetime-local"
                        value={eventsParams.from_date}
                        onChange={(e) => setEventsParams(prev => ({ ...prev, from_date: e.target.value }))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 dark:text-gray-400">To Date</label>
                      <input
                        type="datetime-local"
                        value={eventsParams.to_date}
                        onChange={(e) => setEventsParams(prev => ({ ...prev, to_date: e.target.value }))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>

                  {/* UHF/OBU Filters */}
                  <div className="border-t border-gray-300 dark:border-gray-600 pt-2 mt-2">
                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">🔍 Filter by Device</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-gray-600 dark:text-gray-400">UHF Card ID</label>
                        <input
                          type="text"
                          value={eventsParams.uhf_card_id}
                          onChange={(e) => setEventsParams(prev => ({ ...prev, uhf_card_id: e.target.value }))}
                          placeholder="e.g., UHF1234567890"
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 dark:text-gray-400">OBU ID</label>
                        <input
                          type="text"
                          value={eventsParams.obu_id}
                          onChange={(e) => setEventsParams(prev => ({ ...prev, obu_id: e.target.value }))}
                          placeholder="e.g., OBU99887766"
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div>
                        <label className="text-xs text-gray-600 dark:text-gray-400">Plate Number</label>
                        <input
                          type="text"
                          value={eventsParams.plate_number}
                          onChange={(e) => setEventsParams(prev => ({ ...prev, plate_number: e.target.value }))}
                          placeholder="e.g., AB-1234"
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 dark:text-gray-400">Gate/Terminal ID</label>
                        <input
                          type="text"
                          value={eventsParams.gate_id}
                          onChange={(e) => setEventsParams(prev => ({ ...prev, gate_id: e.target.value }))}
                          placeholder="e.g., GATE-01"
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleTestEvents}
                  disabled={testLoading.events}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-4 py-2 rounded-lg mb-3"
                >
                  {testLoading.events ? '⏳ Testing...' : '📋 Test Events'}
                </button>
                {testResults.events && (
                  <div className={`p-3 rounded-lg ${testResults.events.success
                      ? 'bg-green-100 dark:bg-green-900'
                      : 'bg-red-100 dark:bg-red-900'
                    }`}>
                    <div className="text-xs font-mono text-gray-900 dark:text-white mb-1">
                      {testResults.events.timestamp}
                    </div>
                    <div className="text-sm font-semibold mb-2">
                      {testResults.events.success ? '✅ Success' : '❌ Failed'}
                    </div>
                    <pre className="text-xs overflow-auto max-h-40 bg-white dark:bg-gray-800 p-2 rounded">
                      {JSON.stringify(testResults.events.success ? testResults.events.data : testResults.events.error, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              {/* 4. STATS Test */}
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  📊 4. Get Statistics
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Endpoint: GET /api/barriers/{'{id}'}/stats
                </p>

                <div className="space-y-2 mb-3">
                  <div>
                    <label className="text-xs text-gray-600 dark:text-gray-400">Period</label>
                    <select
                      value={statsParams.period}
                      onChange={(e) => setStatsParams(prev => ({ ...prev, period: e.target.value }))}
                      className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="day">Day</option>
                      <option value="week">Week</option>
                      <option value="month">Month</option>
                      <option value="year">Year</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-600 dark:text-gray-400">From Date</label>
                      <input
                        type="date"
                        value={statsParams.from_date}
                        onChange={(e) => setStatsParams(prev => ({ ...prev, from_date: e.target.value }))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 dark:text-gray-400">To Date</label>
                      <input
                        type="date"
                        value={statsParams.to_date}
                        onChange={(e) => setStatsParams(prev => ({ ...prev, to_date: e.target.value }))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleTestStats}
                  disabled={testLoading.stats}
                  className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white px-4 py-2 rounded-lg mb-3"
                >
                  {testLoading.stats ? '⏳ Testing...' : '📊 Test Stats'}
                </button>
                {testResults.stats && (
                  <div className={`p-3 rounded-lg ${testResults.stats.success
                      ? 'bg-green-100 dark:bg-green-900'
                      : 'bg-red-100 dark:bg-red-900'
                    }`}>
                    <div className="text-xs font-mono text-gray-900 dark:text-white mb-1">
                      {testResults.stats.timestamp}
                    </div>
                    <div className="text-sm font-semibold mb-2">
                      {testResults.stats.success ? '✅ Success' : '❌ Failed'}
                    </div>
                    <pre className="text-xs overflow-auto max-h-40 bg-white dark:bg-gray-800 p-2 rounded">
                      {JSON.stringify(testResults.stats.success ? testResults.stats.data : testResults.stats.error, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowTestPanel(false)}
                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
